import { ArrowRightOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Space } from 'antd'
import { FC, useCallback, useContext } from 'react'
import {
    useCreateCalculationPositionMutation,
    useDeleteCalculationPositionMutation,
} from '../../../../store/api/calculation-positions-api'
import { calculationsApi } from '../../../../store/api/calculations-api'
import CalculationPosition from '../../../../types/api/calculation-position'
import _ from 'lodash'
import { useUpdateCalculationMutation } from '../../../../store/api/calculations-api'
import { StrapiCreate, StrapiUpdate } from '../../../../types/strapi/strapi'
import Calculation from '../../../../types/api/calculation'
import connect from '../../../../helpers/api/connect'
import useSuccessModal from '../../../../hooks/api/use-success-modal'
import { PopconfirmProps } from 'antd/lib'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import { CalculationPageCtx } from '../../calculations-page'
import { useAppDispatch, useAppSelector } from '../../../../store/store'
import { selectIsPositionEditing } from '../../../../store/calculations/editing-positions-slice'

const SCALAR_FIELDS = [
    'comment',
    'copies',
    'whitewash',
    'lacquersDms',
    'dmsReserve',
    'cmyk',
    'pantones',
    'sellingPrice',
    'quantityPerRoll',
    'accumulation',
    'currencyRate',
    'stampFloods',
    'numberOfForms',
    'paintCostOf1000DurstWG',
    'uiColor',
    'shipmentType',
] as const

interface CalculationPositionActionsProps {
    position: CalculationPosition & {
        calculation: Calculation
    }
}

const CalculationPositionActions: FC<CalculationPositionActionsProps> = ({ position }) => {
    const { onOpenDesignerTaskModal } = useContext(CalculationPageCtx)
    const dispatch = useAppDispatch()
    const isPositionEditing = useAppSelector(state =>
        selectIsPositionEditing(state, position.documentId)
    )
    const [deletePosition, deletePositionState] = useDeleteCalculationPositionMutation()
    const [createPosition, createPositionState] = useCreateCalculationPositionMutation()
    const [updateCalculation, updateCalculationState] = useUpdateCalculationMutation()
    useSuccessModal(deletePositionState, 'Позиция удалена')
    useErrorModal(deletePositionState, 'Ошибка удаления позиции')
    useErrorModal(createPositionState, 'Ошибка копирования позиции')
    useErrorModal(updateCalculationState, 'Ошибка обновления расчёта')

    const getPositionData = (): StrapiCreate<CalculationPosition>['data'] => {
        const data: StrapiCreate<CalculationPosition>['data'] = {
            ..._.pick(position, SCALAR_FIELDS),
            material_type: connect(position.material_type),
            additional_material_types: connect(position.additional_material_types),
            stamp: connect(position.stamp),
            machine_brands: connect(position.machine_brands),
            machine: connect(position.machine),
            additional_machines: connect(position.additional_machines),
            material: connect(position.material),
            additional_materials: connect(position.additional_materials),
            additional_jobs: connect(position.additional_jobs),
            ...(position.lacquers && {
                lacquers: position.lacquers.map(l => ({
                    type: connect(l.type),
                    name: l.name,
                })),
            }),
            ...(position.calculated && {
                calculated: _.omit(position.calculated, [
                    'id',
                ]) as StrapiCreate<CalculationPosition>['data']['calculated'],
            }),
        }
        return data
    }

    const getRelationsSet = (positionDocId: string): string[] => {
        const positions = position.calculation.calculation_positions ?? []
        const result = positions.reduce<string[]>((acc, item) => {
            acc.push(item.documentId)
            if (item.documentId === position.documentId) {
                acc.push(positionDocId)
            }
            return acc
        }, [])
        return result.length > 0 ? result : [positionDocId]
    }

    const getUpdateCalculationQueryArg = (positionDocId: string): StrapiUpdate<Calculation> => {
        return {
            docId: position.calculation?.documentId || '',
            body: {
                data: {
                    calculation_positions: {
                        set: getRelationsSet(positionDocId),
                    },
                },
            },
        }
    }

    const onCopyPosition = async () => {
        const positionData = getPositionData()
        const result = await createPosition({ data: positionData })
        const positionDocId = result.data?.data?.documentId
        if (!positionDocId) return

        await updateCalculation(getUpdateCalculationQueryArg(positionDocId))
        dispatch(calculationsApi.util.invalidateTags(['Calculations']))
    }

    const confirm: PopconfirmProps['onConfirm'] = () => {
        deletePosition(position.documentId)
    }

    const onSendToShipments = useCallback(() => {
        const calculation = { ...position.calculation, calculation_positions: [position] }
        onOpenDesignerTaskModal(calculation)
    }, [onOpenDesignerTaskModal, position])

    return (
        <Space>
            <Button
                size='small'
                title='Копировать позицию'
                icon={<CopyOutlined />}
                onClick={onCopyPosition}
                color='blue'
                variant='outlined'
            />
            <Button
                size='small'
                title={
                    isPositionEditing
                        ? 'Дождитесь сохранения изменений'
                        : 'Перенести в отгрузки всю сборку или одну позицию'
                }
                icon={<ArrowRightOutlined />}
                color='blue'
                variant='solid'
                disabled={isPositionEditing}
                onClick={onSendToShipments}
            />
            <Popconfirm
                title='Удалить позицию'
                description='Вы уверены?'
                onConfirm={confirm}
                okText='Да'
                cancelText='Нет'
            >
                <Button
                    size='small'
                    title='Удалить позицию'
                    icon={<DeleteOutlined />}
                    danger
                    color='danger'
                    variant='solid'
                />
            </Popconfirm>
        </Space>
    )
}

export default CalculationPositionActions
